import { h } from '@omni-torn/ui';
import {
  cleanEntry,
  PRICELIST_ABOUT_MAX,
  PRICELIST_MAX_ITEMS,
  PRICELIST_NOTE_MAX,
  PRICELIST_PCT_MAX,
  PRICELIST_PCT_MIN,
  pctOfMarket,
  priceFor,
  pricelistPath,
  searchItems,
  type DirectoryAnswer,
  type DirectoryRow,
  type PricedEntry,
  type PricelistEntry,
  type PricelistMine,
  type PricelistSaveAnswer,
  type PricelistView,
} from '@omni-torn/shared';
import type { RefItem } from '@omni-torn/shared/torn-ref';
import { navigateTo } from '../nav';
import { server, session } from '../session';
import { ago, box, ext, loading, money, num, problem, profileUrl, table } from '../ui';
import { ref } from './torn';

/**
 * Pricelists (website roadmap S33 — weav3r's Pricelists and trader discovery; terms version 16): a trader's own list of
 * what they pay, per item a fixed price or a percentage of the market value that follows it, with notes, open or closed.
 *
 * - `/pricelist` — the trader directory (open lists; most items or most recently updated).
 * - `/pricelist/{id}` — one trader's list with the price now, % of market value and when each was last changed.
 * - `/pricelist/manage` — your own list (kept on the Omni-Torn server only once you agreed to terms version 16).
 * Everything comes from the Omni-Torn server (`/api/pricelists*`, apps/api/src/routes/pricelists.ts); invite-only like
 * every section. Links to Torn are plain links the player clicks; nothing here trades.
 */

const pct = (p: number | null): string => (p === null ? '—' : `${num(p, 1)}%`);
const how = (e: Pick<PricelistEntry, 'mode' | 'value'>): string => (e.mode === 'pct' ? `${num(e.value, 1)}% of market value` : 'fixed');

function tabsBar(current: 'traders' | 'mine'): HTMLElement {
  const link = (id: string, label: string, href: string) => h('a', { href, 'data-link': true, class: `btn-small${id === current ? ' active' : ''}`, 'aria-current': id === current ? 'page' : 'false', 'data-pl-tab': id }, label);
  return h('nav', { class: 'toolbar', 'aria-label': 'Pricelists' }, link('traders', 'Traders', '/pricelist'), link('mine', 'Your list', '/pricelist/manage'));
}

export async function renderPricelists(view: HTMLElement, path: string): Promise<void> {
  const id = Number(/^\/pricelist\/(\d+)\/?$/.exec(path)?.[1] ?? 0) || null;
  const manage = /^\/pricelist\/manage\/?$/.test(path);
  const body = h('div', {}, loading('Reading the pricelists…'));
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Pricelists')), tabsBar(manage ? 'mine' : 'traders'), body);
  if (manage) return manageView(body);
  if (id) return traderView(body, id);
  return directoryView(body);
}

// ---------------------------------------------------------------- the directory

async function directoryView(body: HTMLElement): Promise<void> {
  const q = new URLSearchParams(location.search);
  const sort = h('select', { 'data-filter': 'sort', 'aria-label': 'Order' }, h('option', { value: 'items' }, 'Most items'), h('option', { value: 'updated' }, 'Recently updated')) as HTMLSelectElement;
  sort.value = q.get('sort') === 'updated' ? 'updated' : 'items';
  const out = h('div', {}, loading('Reading the traders…'));
  body.replaceChildren(box('Traders buying now', [h('div', { class: 'toolbar' }, h('label', {}, 'Order ', sort)), out], { id: 'pricelist-directory' }));
  const load = async () => {
    history.replaceState(null, '', `/pricelist${sort.value === 'updated' ? '?sort=updated' : ''}`);
    let a: DirectoryAnswer;
    try {
      a = await server<DirectoryAnswer>('GET', `/api/pricelists?sort=${sort.value}`);
    } catch (err) {
      out.replaceChildren(problem(err));
      return;
    }
    if (!body.isConnected) return;
    out.replaceChildren(
      table(
        a.rows,
        [
          { label: 'Trader', get: (r: DirectoryRow) => h('a', { href: pricelistPath(r.traderId), 'data-link': true, 'data-trader': String(r.traderId) }, `${r.traderName ?? 'Player'} [${r.traderId}]`), sort: (r) => r.traderName ?? '' },
          { label: 'Items', get: (r) => num(r.items), sort: (r) => r.items, cls: 'r' },
          { label: 'Updated', get: (r) => h('span', { class: 'muted small' }, ago(r.updatedAt)), sort: (r) => r.updatedAt, cls: 'r' },
          { label: 'About', get: (r) => (r.about ? h('span', { class: 'small pl-note' }, r.about) : h('span', { class: 'muted' }, '—')) },
        ],
        { empty: 'No trader has an open list yet — be the first: “Your list” above.' },
      ),
      h('p', { class: 'muted small', 'data-note': 'directory' }, `${num(a.total)} open list${a.total === 1 ? '' : 's'}${a.total > a.rows.length ? ` (the first ${num(a.rows.length)} shown)` : ''}. Traders’ own lists, kept on Omni-Torn’s server from terms version ${a.termsNeeded} and shown to Omni-Torn users. Omni-Torn never trades for you: open a trade in Torn yourself.`),
    );
  };
  sort.addEventListener('change', () => void load());
  await load();
}

// ---------------------------------------------------------------- one trader's list

function listTable(list: PricelistView, filter = ''): HTMLElement {
  const f = filter.trim().toLowerCase();
  const rows = f ? list.items.filter((e) => (e.itemName ?? '').toLowerCase().includes(f)) : list.items;
  return table(
    rows,
    [
      { label: 'Item', get: (e: PricedEntry) => h('a', { href: `/market/${e.itemId}`, 'data-link': true }, e.itemName ?? `#${e.itemId}`), sort: (e) => e.itemName ?? '' },
      { label: 'Pays now', get: (e) => h('strong', { 'data-price': e.price === null ? '' : String(e.price) }, money(e.price)), sort: (e) => e.price ?? 0, cls: 'r' },
      { label: 'Price', get: (e) => h('span', { class: 'small' }, e.mode === 'fixed' ? `${money(e.value)} fixed` : how(e)), sort: (e) => (e.mode === 'pct' ? e.value : 1_000 + e.value) },
      { label: 'Market value', get: (e) => h('span', { title: e.marketFrom === 'pool' ? 'Torn has no market value: the cheapest listing Omni-Torn users saw in the last 2 hours' : 'Torn’s market value' }, money(e.marketValue), e.marketFrom === 'pool' ? h('span', { class: 'muted small' }, ' (seen)') : ''), sort: (e) => e.marketValue ?? 0, cls: 'r' },
      { label: '% of value', get: (e) => h('span', { 'data-pct': e.pctOfMarket === null ? '' : String(e.pctOfMarket) }, pct(e.pctOfMarket)), sort: (e) => e.pctOfMarket ?? 0, cls: 'r' },
      { label: 'Note', get: (e) => (e.note ? h('span', { class: 'small pl-note' }, e.note) : h('span', { class: 'muted' }, '—')) },
      { label: 'Changed', get: (e) => h('span', { class: 'muted small' }, ago(e.updatedAt)), sort: (e) => e.updatedAt, cls: 'r' },
    ],
    { empty: f ? 'No item by that name on this list.' : 'Nothing on this list yet.' },
  );
}

function listHead(list: PricelistView): HTMLElement {
  return h(
    'div',
    { class: 'pl-head' },
    h('p', {}, h('span', { class: `badge${list.open ? ' on' : ''}`, 'data-open': list.open ? '1' : '0' }, list.open ? 'Buying now' : 'Closed'), ' ', ext(profileUrl(list.traderId), `${list.traderName ?? 'Player'} [${list.traderId}] on Torn`), h('span', { class: 'muted small' }, ` · ${num(list.items.length)} item${list.items.length === 1 ? '' : 's'} · updated ${ago(list.updatedAt)}`)),
    list.about ? h('p', { class: 'pl-note', 'data-about': '' }, list.about) : null,
  );
}

async function traderView(body: HTMLElement, id: number): Promise<void> {
  let list: PricelistView;
  try {
    list = await server<PricelistView>('GET', `/api/pricelists/${id}`);
  } catch (err) {
    body.replaceChildren(box('Pricelist', [problem(err), h('p', {}, h('a', { href: '/pricelist', 'data-link': true }, 'Every trader with an open list'))], { id: 'pricelist' }));
    return;
  }
  if (!body.isConnected) return;
  document.title = `${list.traderName ?? 'Player'}’s pricelist · Omni-Torn`;
  const filter = h('input', { type: 'search', placeholder: 'Find an item', maxlength: '40', 'data-filter': 'item', 'aria-label': 'Find an item' }) as HTMLInputElement;
  const out = h('div', {}, listTable(list));
  filter.addEventListener('input', () => out.replaceChildren(listTable(list, filter.value)));
  const own = session.me()?.playerId === id;
  body.replaceChildren(
    box(
      `${list.traderName ?? 'Player'}’s pricelist`,
      [
        listHead(list),
        h('div', { class: 'toolbar' }, filter, own ? h('a', { href: '/pricelist/manage', 'data-link': true, class: 'btn-small' }, 'Change your list') : null),
        out,
        h('p', { class: 'muted small', 'data-note': 'pricelist' }, `What this trader says they pay for one. A percentage follows Torn’s market value${list.pricedAt ? ` (read ${ago(list.pricedAt)})` : ''}. Omni-Torn never trades for you — open a trade in Torn yourself; the trade helper shows this list’s prices on a trade with them.`),
      ],
      { id: 'pricelist' },
    ),
  );
}

// ---------------------------------------------------------------- your own list

async function manageView(body: HTMLElement): Promise<void> {
  let mine: PricelistMine;
  let items: readonly RefItem[] = [];
  try {
    const [m, it] = await Promise.all([server<PricelistMine>('GET', '/api/pricelists/mine'), ref('items').catch(() => null)]);
    mine = m;
    items = it?.data ?? [];
  } catch (err) {
    body.replaceChildren(problem(err));
    return;
  }
  if (!body.isConnected) return;
  const byId = new Map(items.map((i) => [i.id, i]));
  const tradable = items.filter((i) => i.tradable !== false);
  const me = session.me()?.playerId ?? 0;
  let entries: PricelistEntry[] = (mine.list?.items ?? []).map((e) => ({ itemId: e.itemId, mode: e.mode, value: e.value, note: e.note }));

  if (!mine.mayStore && !mine.list) {
    body.replaceChildren(
      box(
        'Your pricelist',
        [
          h('p', { 'data-note': 'pricelist-terms' }, `Keeping a pricelist opens with Omni-Torn’s terms version ${mine.termsNeeded} (you agreed to ${mine.terms || 1}): your list — items, prices, notes, open or closed — is kept on Omni-Torn’s server and shown to Omni-Torn users until you remove it or delete your data.`),
          h('p', { class: 'muted' }, 'When Omni-Torn’s terms change, a line at the top of the page asks once; the whole table is at ', h('a', { href: '/terms', 'data-link': true }, '/terms'), '.'),
        ],
        { id: 'pricelist-manage' },
      ),
    );
    return;
  }

  const openIn = h('input', { type: 'checkbox', 'data-pl': 'open' }) as HTMLInputElement;
  openIn.checked = mine.list?.open ?? true;
  const aboutIn = h('textarea', { maxlength: String(PRICELIST_ABOUT_MAX), rows: '2', placeholder: 'How to trade with you (optional)', 'data-pl': 'about', class: 'pl-about' }) as HTMLTextAreaElement;
  aboutIn.value = mine.list?.about ?? '';
  const nameIn = h('input', { type: 'search', list: 'pl-items', placeholder: 'Item name', maxlength: '40', 'data-pl': 'item', 'aria-label': 'Item' }) as HTMLInputElement;
  const modeIn = h('select', { 'data-pl': 'mode', 'aria-label': 'Price as' }, h('option', { value: 'pct' }, '% of market value'), h('option', { value: 'fixed' }, 'Fixed $')) as HTMLSelectElement;
  const valueIn = h('input', { type: 'number', min: '1', step: 'any', placeholder: '95', class: 'num-input', 'data-pl': 'value', 'aria-label': 'Price or percent' }) as HTMLInputElement;
  const noteIn = h('input', { type: 'text', maxlength: String(PRICELIST_NOTE_MAX), placeholder: 'Note (optional)', 'data-pl': 'note', 'aria-label': 'Note', class: 'pl-note-in' }) as HTMLInputElement;
  const addMsg = h('span', { class: 'muted small', role: 'status' });
  const saveMsg = h('p', { class: 'form-status', role: 'status', 'data-pl': 'status' });
  const listOut = h('div', {});
  const saveBtn = h('button', { type: 'button', class: 'btn-primary', 'data-pl': 'save' }, 'Save your list') as HTMLButtonElement;
  const removeBtn = h('button', { type: 'button', class: 'btn-small', 'data-pl': 'remove' }, 'Remove your list') as HTMLButtonElement;
  let removeArmed = false;

  const markDirty = () => {
    saveMsg.className = 'form-status';
    saveMsg.textContent = 'Not saved yet.';
  };
  const drawList = () => {
    const rows = entries.map((e) => {
      const it = byId.get(e.itemId);
      const price = priceFor(e, it?.marketValue ?? null);
      return { e, name: it?.name ?? `#${e.itemId}`, price, pctOf: pctOfMarket(price, it?.marketValue ?? null) };
    });
    listOut.replaceChildren(
      table(
        rows,
        [
          { label: 'Item', get: (r) => r.name, sort: (r) => r.name },
          { label: 'Price', get: (r) => (r.e.mode === 'fixed' ? `${money(r.e.value)} fixed` : how(r.e)) },
          { label: 'Pays now', get: (r) => money(r.price), sort: (r) => r.price ?? 0, cls: 'r' },
          { label: '% of value', get: (r) => pct(r.pctOf), sort: (r) => r.pctOf ?? 0, cls: 'r' },
          { label: 'Note', get: (r) => (r.e.note ? h('span', { class: 'small pl-note' }, r.e.note) : h('span', { class: 'muted' }, '—')) },
          {
            label: '',
            get: (r) =>
              h(
                'span',
                { class: 'pl-row-actions' },
                h('button', { type: 'button', class: 'btn-small', 'data-pl-edit': String(r.e.itemId), onclick: () => ((nameIn.value = r.name), (modeIn.value = r.e.mode), (valueIn.value = String(r.e.value)), (noteIn.value = r.e.note), nameIn.focus()) }, 'Edit'),
                h('button', { type: 'button', class: 'btn-small', 'data-pl-drop': String(r.e.itemId), onclick: () => ((entries = entries.filter((x) => x.itemId !== r.e.itemId)), markDirty(), drawList()) }, 'Remove'),
              ),
          },
        ],
        { empty: 'Nothing on your list yet: add an item and what you pay for it.' },
      ),
      h('p', { class: 'muted small' }, `${num(entries.length)} of at most ${num(PRICELIST_MAX_ITEMS)} items. “Pays now” uses Torn’s market value today; a percentage follows it as it moves.`),
    );
  };

  const add = h(
    'form',
    {
      class: 'toolbar',
      'data-pl': 'add',
      onsubmit: (e: Event) => {
        e.preventDefault();
        const it = searchItems(tradable, nameIn.value, 1)[0];
        if (!it) return void (addMsg.textContent = 'No tradable item by that name.');
        const entry = cleanEntry({ itemId: it.id, mode: modeIn.value, value: valueIn.value, note: noteIn.value });
        if (!entry) return void (addMsg.textContent = modeIn.value === 'pct' ? `A percentage from ${PRICELIST_PCT_MIN} to ${PRICELIST_PCT_MAX}.` : 'A price of $1 or more.');
        const had = entries.some((x) => x.itemId === it.id);
        if (!had && entries.length >= PRICELIST_MAX_ITEMS) return void (addMsg.textContent = `A list holds at most ${PRICELIST_MAX_ITEMS} items.`);
        entries = had ? entries.map((x) => (x.itemId === it.id ? entry : x)) : [...entries, entry];
        addMsg.textContent = `${had ? 'Changed' : 'Added'}: ${it.name}.`;
        nameIn.value = '';
        valueIn.value = '';
        noteIn.value = '';
        markDirty();
        drawList();
      },
    },
    nameIn,
    h('datalist', { id: 'pl-items' }, ...tradable.map((i) => h('option', { value: i.name }))),
    modeIn,
    valueIn,
    noteIn,
    h('button', { type: 'submit' }, 'Add / change'),
    addMsg,
  );

  saveBtn.addEventListener('click', async () => {
    saveBtn.disabled = true;
    saveMsg.className = 'form-status';
    saveMsg.textContent = 'Saving…';
    try {
      const r = await server<PricelistSaveAnswer>('PUT', '/api/pricelists/mine', { open: openIn.checked, about: aboutIn.value, items: entries });
      saveMsg.className = 'form-status ok';
      saveMsg.replaceChildren(`Saved — ${num(r.list?.items.length ?? entries.length)} item${(r.list?.items.length ?? entries.length) === 1 ? '' : 's'}, ${openIn.checked ? 'open' : 'closed'}. `, h('a', { href: pricelistPath(me), 'data-link': true, 'data-pl': 'view' }, 'See it as others do'));
    } catch (err) {
      saveMsg.className = 'form-status bad';
      saveMsg.textContent = err instanceof Error ? err.message : 'Couldn’t save.';
    } finally {
      saveBtn.disabled = false;
    }
  });
  removeBtn.addEventListener('click', async () => {
    if (!removeArmed) {
      removeArmed = true;
      removeBtn.textContent = 'Click again to remove it';
      return;
    }
    removeBtn.disabled = true;
    try {
      await server('DELETE', '/api/pricelists/mine');
      navigateTo('/pricelist/manage');
    } catch (err) {
      saveMsg.className = 'form-status bad';
      saveMsg.textContent = err instanceof Error ? err.message : 'Couldn’t remove it.';
      removeBtn.disabled = false;
    }
  });
  openIn.addEventListener('change', markDirty);
  aboutIn.addEventListener('input', markDirty);

  drawList();
  body.replaceChildren(
    box(
      'Your pricelist',
      [
        !mine.mayStore ? h('p', { class: 'notice', 'data-note': 'pricelist-terms' }, `Saving needs Omni-Torn’s terms version ${mine.termsNeeded} (you agreed to ${mine.terms || 1}).`) : null,
        h('label', { class: 'pl-open' }, openIn, ' Open — buying now (shown in the traders list and on trades with you)'),
        aboutIn,
        add,
        listOut,
        h('div', { class: 'toolbar' }, saveBtn, mine.list ? removeBtn : null, mine.list ? h('a', { href: pricelistPath(me), 'data-link': true }, 'See it as others do') : null),
        saveMsg,
        h('p', { class: 'muted small' }, 'Your list is kept on Omni-Torn’s server and shown to Omni-Torn users (this page’s Traders list, your list’s page, and the extension’s trade helper on a trade with you) until you remove it or delete your data. Omni-Torn never trades for you.'),
      ],
      { id: 'pricelist-manage' },
    ),
  );
}
